/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { clone } from '@jack-henry/frontend-utils/functions';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { NavigationService } from '@treasury/core/navigation';
import { AchService } from '@treasury/domain/ach';
import { TmDetailedError } from '@treasury/domain/shared';
import { UsersService } from '@treasury/domain/users';
import { TmContainer, TmFooterService } from '@treasury/presentation';
import '@treasury/presentation/components/tm-list-header';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-loader';
import { type SummaryTileVm } from '@treasury/presentation/components/tm-summary-tile';
import '@treasury/presentation/components/vendor/tm-pagination-controls';
import { TmPaginationControls } from '@treasury/presentation/components/vendor/tm-pagination-controls';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { exists } from '@treasury/utils';
import { ContextMenuItem } from '@vaadin/context-menu';
import '@vaadin/grid';
import { GridActiveItemChangedEvent, GridSelectedItemsChangedEvent } from '@vaadin/grid';
import { columnBodyRenderer, GridColumnBodyLitRenderer } from '@vaadin/grid/lit.js';
import '@vaadin/grid/vaadin-grid-column.js';
import '@vaadin/grid/vaadin-grid-sort-column.js';
import { css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { filterTableResults } from '../../../utilities/table-result-filter';
import '../../components/jhd-status';
import { getStatusColorToken } from '../../components/jhd-status';
import '../../components/templates/treasury-status-column.template';
import { AchPaymentTableViewModel, AchPaymentViewModel } from '../data/ach-payment-view-model';
import { AchPayment } from '../types/ach-payment.type';

export const tagName = 'ach-payments-table';
@customElement(tagName)
export class AchPaymentsTable extends TmContainer {
    @property({ type: Array })
    public achPayments = [];

    @property({ type: Array })
    public totals?: { totalCredit: number; totalDebit: number; count: number; status: string }[];

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private readonly footerService: TmFooterService;

    @InjectProperty()
    declare private usersService: UsersService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @InjectProperty()
    declare private readonly achService: AchService;

    @state()
    private filteredAchPayments: AchPayment[] = [];

    @state()
    private achPaymentViewModels: AchPaymentViewModel[] = [];

    @state()
    private filteredAchPaymentViewModels: AchPaymentViewModel[] = [];

    @state()
    private paginatedAchPaymentViewModels: AchPaymentViewModel[] = [];

    @state()
    private selectedAchPayments: AchPaymentViewModel[] = [];

    @state()
    private paginatedAchPaymentsViewModelsCount = 0;

    @state()
    private fieldsToFilter = [
        'name',
        'status',
        'creditAmount',
        'debitAmount',
        'transactionId',
        'paymentDate',
    ];

    @state()
    private selectedTile?: SummaryTileVm;

    @state()
    private searchTerm = '';

    @query('tm-pagination-controls')
    private paginationControls!: TmPaginationControls;

    public disconnectedCallback() {
        super.disconnectedCallback?.();
        this.footerService.hide();
    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('achPayments')) {
            this.filteredAchPayments = this.achPayments;
            this.paginatedAchPaymentsViewModelsCount = this.achPayments.length;
            this.buildAchPaymentViewModels();
        }

        if (changedProperties.has('achPaymentViewModels')) {
            this.selectedTile = undefined;
            this.searchTerm = '';
            this.filterResults({ detail: { value: this.searchTerm } } as CustomEvent);
        }
        this.configureFooter();
    }

    protected render() {
        return html`<div class="flex flex-col h-full">
            <div class="flex-none">
                <tm-list-header
                    .filterButton=${true}
                    .searchTerm=${this.searchTerm}
                    @filterButtonClick=${() =>
                        this.dispatchEvent(new CustomEvent('filter-ach-payments'))}
                    @search-term-change=${(e: TmSearchTermChangeEvent) => this.filterResults(e)}
                >
                </tm-list-header>
                ${this.renderTotals()}
            </div>
            <div class="flex-grow">${this.renderTable()}</div>
            ${this.isDesktop
                ? html`<div class="flex-none">
                      <tm-pagination-controls
                          .totalItemCount=${this.paginatedAchPaymentsViewModelsCount}
                          @page-changed=${() => {
                              this.handlePagination();
                          }}
                      ></tm-pagination-controls>
                  </div>`
                : nothing}
        </div> `;
    }

    private renderTotals() {
        if (!this.totals || this.totals.length === 0 || !this.isDesktop) {
            return nothing;
        }
        const tiles = this.totals.map(total => ({
            ...total,
            totalAmount: html`<div class="flex justify-between gap-2 w-full">
                <div>
                    ${total.totalCredit.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                    })}
                    <small>(CR)</small>
                </div>
                <div>
                    ${total.totalDebit.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                    })}
                    <small>(DR)</small>
                </div>
            </div>`,
        }));
        return html`<list-summary-bar
            .summary=${tiles}
            .selectedTile=${this.selectedTile}
            @tileClicked=${(e: CustomEvent) => {
                const status = e.detail.id;
                if (this.selectedTile?.title === status) {
                    this.selectedTile = undefined;
                    this.filteredAchPaymentViewModels = this.achPaymentViewModels;
                    this.paginatedAchPaymentViewModels = this.achPaymentViewModels;
                    this.handlePagination();
                    return;
                }
                this.selectedTile = e.detail;
                this.filterResults(status);
            }}
        ></list-summary-bar>`;
    }

    private buildAchPaymentViewModels() {
        this.achPaymentViewModels = this.achPayments.map(
            achPayment => new AchPaymentTableViewModel(achPayment)
        );
        this.filteredAchPaymentViewModels = this.achPaymentViewModels;
    }

    private filterResults(e: CustomEvent) {
        if (typeof e === 'string') {
            this.searchTerm = '';
        } else {
            this.searchTerm = e.detail.value;
        }
        this.filteredAchPaymentViewModels = filterTableResults(
            e,
            this.achPaymentViewModels,
            this.fieldsToFilter
        );
        this.paginatedAchPaymentViewModels = this.filteredAchPaymentViewModels;
        if (this.isDesktop) {
            this.handlePagination();
        }
    }

    private handlePagination() {
        const paginatedAchPayments = clone(this.paginatedAchPaymentViewModels);
        const offset = this.paginationControls?.calculateOffset();
        const limit = this.paginationControls?.pageSize;
        this.paginatedAchPaymentsViewModelsCount = paginatedAchPayments.length;
        this.filteredAchPaymentViewModels = paginatedAchPayments.slice(offset, offset + limit);
    }

    private renderStatusIconColumn(achPayment: AchPaymentViewModel) {
        return html`<jhd-status .status=${achPayment.status}></jhd-status>`;
    }

    private renderPaymentNameAndStatusColumn(achPayment: AchPaymentViewModel) {
        return html`<div class="flex -ml-4">
            <div class="flex flex-col">
                <span class="payment-name text-sm font-medium">${achPayment.name}</span>
                <small class="payment-transaction-id text-xs text-tertiary">
                    ${achPayment.transactionId}
                </small>
                <small
                    class="payment-status text-xs"
                    style=${`color: var(${getStatusColorToken(achPayment.status)})`}
                    >${achPayment.status}</small
                >
            </div>
        </div>`;
    }

    private renderAmountAndAccountColumn(achPayment: AchPaymentViewModel) {
        const amounts = [];
        if (achPayment.creditAmount) {
            const creditAmount = html`
                <div class="font-medium text-sm ">
                    ${achPayment.creditAmount}
                    <span class="text-xs font-normal">CR</span>
                </div>
            `;
            amounts.push(creditAmount);
        }

        if (achPayment.debitAmount) {
            const debitAmount = html`
                <div class="font-medium text-sm ">
                    ${achPayment.debitAmount}
                    <span class="text-xs font-normal">DR</span>
                </div>
            `;
            amounts.push(debitAmount);
        }

        return html`
            <div class="flex flex-col pr-1 items-end">
                ${amounts}
                <small class="payment-transaction-id text-right text-xs text-secondary">
                    ${achPayment.frequencyDisplay}
                </small>
            </div>
        `;
    }

    private renderTable() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        if (!this.filteredAchPayments.length) {
            return html`
                <div
                    class=${classMap({
                        'w-full px-4 py-8 bg-primary text-center text-sm text-secondary': true,
                        'shadow-md': !this.isDesktop,
                    })}
                >
                    No ACH payments found
                </div>
            `;
        }
        const isSelectable = (item: AchPaymentViewModel) => {
            return (
                item.status === 'Pending Approval' && item.permissions?.some(p => p === 'Approve')
            );
        };
        return html`
            <vaadin-grid
                class=${classMap({
                    'h-full': true,
                    'shadow-md': !this.isDesktop,
                })}
                .isItemSelectable=${isSelectable}
                .items=${this.filteredAchPaymentViewModels}
                @active-item-changed=${(e: GridActiveItemChangedEvent<AchPaymentViewModel>) => {
                    const { value } = e.detail;
                    if (exists(value)) {
                        this.dispatchEvent(
                            new CustomEvent('ach-payment-select', { detail: value })
                        );
                    }
                }}
                @selected-items-changed="${(
                    e: GridSelectedItemsChangedEvent<AchPaymentViewModel>
                ) => {
                    this.selectedAchPayments = e.detail.value;
                    this.dispatchEvent(
                        new CustomEvent('selection-changed', {
                            detail: this.selectedAchPayments,
                        })
                    );
                }}"
            >
                ${this.renderColumns()}
            </vaadin-grid>
        `;
    }

    private renderColumns() {
        if (!this.deviceService.isDesktop) {
            return html`<vaadin-grid-column
                    flex-grow="0"
                    width="3rem"
                    ${columnBodyRenderer(
                        this
                            .renderStatusIconColumn as GridColumnBodyLitRenderer<AchPaymentViewModel>,
                        []
                    )}
                ></vaadin-grid-column>
                <vaadin-grid-column
                    flex-grow="1"
                    ${columnBodyRenderer(
                        this
                            .renderPaymentNameAndStatusColumn as GridColumnBodyLitRenderer<AchPaymentViewModel>,
                        []
                    )}
                ></vaadin-grid-column>
                <vaadin-grid-column
                    text-align="end"
                    width="auto"
                    ${columnBodyRenderer(
                        this
                            .renderAmountAndAccountColumn as GridColumnBodyLitRenderer<AchPaymentViewModel>,
                        []
                    )}
                ></vaadin-grid-column>`;
        }
        return html`<vaadin-grid-selection-column width="20px" text-align="center">
            </vaadin-grid-selection-column>
            <vaadin-grid-sort-column
                header="Transaction ID"
                path="transactionId"
                ${columnBodyRenderer(
                    (item: AchPaymentViewModel) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.transactionId}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Batch Name"
                path="name"
                ${columnBodyRenderer(
                    (item: AchPaymentViewModel) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.name}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="File Name"
                path="fileName"
                ${columnBodyRenderer(
                    (item: AchPaymentViewModel) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.fileName}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="ACH Company Name"
                path="achCompanyName"
                ${columnBodyRenderer(
                    (item: AchPaymentViewModel) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.achCompanyName}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="SEC Code"
                path="secCode"
                ${columnBodyRenderer(
                    (item: AchPaymentViewModel) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.secCode}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column header="Source" path="source"></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Initiated Date"
                path="createdDate"
                ${columnBodyRenderer(
                    (item: AchPaymentViewModel) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.createdDate}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Effective Date"
                path="paymentDate"
                ${columnBodyRenderer(
                    (item: AchPaymentViewModel) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.paymentDate}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Debit Amount"
                path="debitAmount"
                ${columnBodyRenderer(
                    (item: AchPaymentViewModel) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.debitAmount}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Credit Amount"
                path="creditAmount"
                ${columnBodyRenderer(
                    (item: AchPaymentViewModel) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.creditAmount}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Status"
                path="status"
                ${columnBodyRenderer((payment: AchPaymentViewModel) => {
                    return html`<treasury-status-column
                        .item=${payment}
                        .completedApproversFunction=${async () =>
                            this.usersService.fetchCompletedApprovers({
                                approvalEntity: 'achPayment',
                                productId: payment.id as number | undefined,
                                updatedBy: 0,
                            }) as unknown as string[]}
                        .eligibleApproversFunction=${async () =>
                            this.usersService.fetchAvailableApprovers({
                                approvalEntity: 'achPayment',
                                productId: payment.id as number | undefined,
                                updatedBy: 0,
                            }) as unknown as string[]}
                    ></treasury-status-column>`;
                })}
            ></vaadin-grid-sort-column>
            <vaadin-grid-column
                frozen-to-end
                flex-grow="0"
                width="3rem"
                text-align="end"
                @click=${(e: Event) => e.stopPropagation()}
                ${columnBodyRenderer((payment: AchPaymentViewModel) => {
                    const actions: Array<ContextMenuItem> = this.getAchPaymentActions(payment);
                    return html`<tm-actions-column
                        .items=${actions}
                        @action-selected=${(e: CustomEvent) => {
                            const action = e.detail.value.text;
                            this.handleActionSelected(action, payment);
                        }}
                    ></tm-actions-column>`;
                })}
            ></vaadin-grid-column>`;
    }

    private configureFooter() {
        if (!this.isDesktop) {
            this.footerService.hide();
            return;
        }
        this.footerService.configure({
            direction: 'row',
            buttonConfig: [
                {
                    text: 'Approve',
                    success: true,
                    disabled: !this.selectedAchPayments.length,
                    onClick: () => this.approvePayment(this.selectedAchPayments),
                },
                {
                    text: 'Reject',
                    error: true,
                    disabled: !this.selectedAchPayments.length,
                    onClick: () => this.rejectPayment(this.selectedAchPayments),
                },
            ],
        });
    }

    private getAchPaymentActions(payment: AchPaymentViewModel): Array<ContextMenuItem> {
        const actions: Array<ContextMenuItem> = [];
        const permissions = payment.permissions?.map(p => p) || [];
        if (permissions?.includes('EditPayment')) {
            actions.push({
                text: 'Edit',
            });
        }
        // if (permissions?.includes('DeletePayment')) {
        //     actions.push({
        //         text: 'Delete',
        //     });
        // }
        if (permissions?.includes('Approve')) {
            actions.push({ text: 'Approve' });
        }
        if (permissions?.includes('RejectPayment')) {
            actions.push({ text: 'Reject' });
        }
        return actions;
    }

    private handleActionSelected(action: string, payment: AchPaymentViewModel) {
        switch (action) {
            // case 'EditPayment':
            //     this.navService.navigate(`/ach/payments/edit/${payment.id}`);
            //     break;
            // case 'Delete':
            //     this.deletePayment(payment);
            //     break;
            case 'Initiate Payment':
                this.navService.navigate(`/ach/create/${payment.id}`);
                break;
            case 'Approve':
                this.approvePayment([payment]);
                break;
            case 'RejectPayment':
                this.rejectPayment([payment]);
                break;
            default:
                break;
        }
    }

    private async approvePayment(payments: AchPaymentViewModel[]) {
        const { reason } = await this.dialogService.open(
            `Are you sure you want to approve this payment?`,
            'Confirm Approval',
            {
                buttons: {
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Approve',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                },
                classes: ['max-w-[500px]'],
            }
        );
        if (reason === DynamicComponentExitReason.Confirm) {
            this.loading = true;
            try {
                await this.achService.approveAchPayments(
                    payments.map(payment => payment.id.toString()),
                    ''
                );
                this.dispatchEvent(new CustomEvent('payment-approved', { detail: payments }));
            } catch (error) {
                if (error instanceof TmDetailedError) {
                    this.notificationService.renderError(error);
                } else if (error instanceof Error) {
                    this.notificationService.renderError(error);
                } else {
                    this.notificationService.renderError('Error approving your ACH payment');
                }
            } finally {
                this.loading = false;
            }
        }
        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
    }

    private async rejectPayment(payments: AchPaymentViewModel[]) {
        const { reason } = await this.dialogService.open(
            `Are you sure you want to reject this payment?`,
            'Confirm Rejection',
            {
                buttons: {
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Reject',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                },
                classes: ['max-w-[500px]'],
            }
        );
        if (reason === DynamicComponentExitReason.Confirm) {
            this.loading = true;
            try {
                await this.achService.rejectAchPayments(
                    payments.map(payment => payment.id.toString()),
                    ''
                );
                this.dispatchEvent(new CustomEvent('payment-rejected', { detail: payments }));
            } catch (error) {
                if (error instanceof TmDetailedError) {
                    this.notificationService.renderError(error);
                } else if (error instanceof Error) {
                    this.notificationService.renderError(error);
                } else {
                    this.notificationService.renderError('Error rejecting your ACH payment');
                }
            } finally {
                this.loading = false;
            }
        }
        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
    }

    // TODO: couldn't find an instance of delete or the request in the client
    // private async deletePayment(payment?: AchPaymentViewModel) {
    //     const { reason } = await this.dialogService.open(
    //         `Are you sure you want to delete this payment?`,
    //         'Confirm Deletion',
    //         {
    //             buttons: {
    //                 [TmDialogButtonPosture.Secondary]: {
    //                     label: 'Cancel',
    //                     reason: DynamicComponentExitReason.Cancel,
    //                 },
    //                 [TmDialogButtonPosture.Primary]: {
    //                     label: 'Delete',
    //                     reason: DynamicComponentExitReason.Confirm,
    //                 },
    //             },
    //             classes: ['max-w-[500px]'],
    //         }
    //     );
    //     if (reason === DynamicComponentExitReason.Confirm) {
    //         this.loading = true;
    //         try {
    //             await this.achService.delete(payment?.id as number);
    //             this.navService.navigate('/ach');
    //         } catch (error) {
    //             if (error instanceof TmDetailedError) {
    //                 this.notificationService.renderError(error);
    //             } else if (error instanceof Error) {
    //                 this.notificationService.renderError(error);
    //             } else {
    //                 this.notificationService.renderError('Error deleting your ACH payment');
    //             }
    //         } finally {
    //             this.loading = false;
    //         }
    //     }
    //     if (reason === DynamicComponentExitReason.Cancel) {
    //         return;
    //     }
    // }

    public static get styles() {
        return [
            css`
                button[disabled] {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                .table-header,
                .empty-message {
                    color: var(--header-text-color);
                }

                vaadin-grid::part(body-row) {
                    height: 56px;
                }
                vaadin-grid::part(first-column-cell) {
                    border: none;
                }
                vaadin-grid::part(cell) {
                    align-items: center;
                }
                vaadin-grid::part(header-cell) {
                    min-height: auto;
                    padding-top: 3px;
                    background-color: var(--border-color);
                    font-size: 0.75rem;
                    line-height: calc(1 / 0.75);
                }
                vaadin-grid-sorter::part(indicators) {
                    opacity: 0.5;
                }
                vaadin-grid-sorter[direction]::part(indicators) {
                    opacity: 100;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AchPaymentsTable;
    }
}
